/**
 * Import profiles CRUD (TMMAPP-0013, design §15.1) — wire types from `src/api/generated/`.
 * Mapping edits happen by re-importing; this module lists, renames, scopes, and deletes.
 */
import { request } from './client';
import type {
  ColumnMap as WireColumnMap,
  ImportProfileCreateRequest,
  ImportProfilePatchRequest,
  ImportProfileResponse as WireProfile,
} from './generated';
import type { AmountMode, ColumnMap, OutflowSign } from './imports';

export type ProfileOrigin = 'USER' | 'AI';
export type ProfileScope = 'ACCOUNT' | 'OWNER';

export interface ImportProfile {
  id: number;
  name: string;
  accountId: number | null;
  scope: ProfileScope;
  headerRow: number;
  firstDataRow: number;
  columnMap: ColumnMap;
  amountMode: AmountMode;
  outflowSign: OutflowSign;
  dateFormat: string;
  delimiter: string;
  encoding: string;
  defaultSourceCurrency: string | null;
  filenamePattern: string | null;
  origin: ProfileOrigin;
}

const AMOUNT_MODES: ReadonlySet<string> = new Set(['SIGNED', 'DEBIT_CREDIT']);
const OUTFLOW_SIGNS: ReadonlySet<string> = new Set(['NEGATIVE', 'POSITIVE']);
const ORIGINS: ReadonlySet<string> = new Set(['USER', 'AI']);

function normalizeColumnMap(w: WireColumnMap): ColumnMap {
  return {
    date: w.date ?? null,
    amount: w.amount ?? null,
    debit: w.debit ?? null,
    credit: w.credit ?? null,
    payee: w.payee ?? null,
    memo: w.memo ?? null,
    runningBalance: w.runningBalance ?? null,
    externalId: w.externalId ?? null,
  };
}

/** An absent `accountId` is an OWNER-scoped profile. */
export function normalizeImportProfile(w: WireProfile): ImportProfile {
  const accountId = w.accountId ?? null;
  return {
    id: w.id,
    name: w.name,
    accountId,
    scope: accountId === null ? 'OWNER' : 'ACCOUNT',
    headerRow: w.headerRow,
    firstDataRow: w.firstDataRow,
    columnMap: normalizeColumnMap(w.columnMap),
    amountMode: AMOUNT_MODES.has(w.amountMode) ? w.amountMode : 'SIGNED',
    outflowSign: OUTFLOW_SIGNS.has(w.outflowSign) ? w.outflowSign : 'NEGATIVE',
    dateFormat: w.dateFormat,
    delimiter: w.delimiter,
    encoding: w.encoding,
    defaultSourceCurrency: w.defaultSourceCurrency ?? null,
    filenamePattern: w.filenamePattern ?? null,
    origin: ORIGINS.has(w.origin) ? w.origin : 'USER',
  };
}

export const importProfilesApi = {
  async list(): Promise<ImportProfile[]> {
    const wire = await request<WireProfile[]>('GET', '/v1/me/import-profiles');
    return wire.map(normalizeImportProfile);
  },

  async create(body: ImportProfileCreateRequest): Promise<ImportProfile> {
    return normalizeImportProfile(
      await request<WireProfile>('POST', '/v1/me/import-profiles', { json: body }),
    );
  },

  /** PATCH: send only the keys the person changed. */
  async update(id: number, body: ImportProfilePatchRequest): Promise<ImportProfile> {
    return normalizeImportProfile(
      await request<WireProfile>('PATCH', `/v1/me/import-profiles/${id}`, { json: body }),
    );
  },

  async remove(id: number): Promise<void> {
    await request<void>('DELETE', `/v1/me/import-profiles/${id}`);
  },
};
